/**
 * CellSelectionEntry — the front door of "Cell Selection" (/cell-selection).
 *
 * Two equal-dignity doors (the approved Option A entry redesign):
 *   - Guided · Ion (primary): describe an application OR pick a branch chip →
 *     opens a FRESH /assistant conversation seeded with the branch ("Ask Ion").
 *   - Advanced (equal weight): the classic filter + ranking wizard
 *     (BenchmarkingWorkflow), entered via ?step=fundamentals.
 *
 * MainAppPage renders this on /cell-selection when no wizard ?step is active;
 * the wizard renders once a step is in the URL. Reuses the app's brand tokens +
 * the branch line-icons (shared/Icons.jsx) so it reads as part of the tool, not
 * an AI-generated landing. The fresh-conversation seed mirrors the logic that
 * used to live in IonHeroEntry.gotoAssistant.
 */

import { useEffect, useRef, useState } from 'preact/hooks'
import { useLocation } from 'wouter-preact'

import {
  autoSendDraft,
  clearConversation,
  draftInput,
  ionHidden,
  resetSelectionContext,
  setSelectionContext,
} from '../../state/chatStore.js'
import { currentStep } from '../../state/benchmarkingWorkflowState.js'
import { sidebarVisible } from '../../state/globalSignals.js'
import { appendDemoParam } from '../../services/ionDemoMode.js'
import { GUIDED_SELECTION_ENABLED } from '../../config/featureFlags.js'
import { getCellInfo } from '../../services/subscriptionService.js'
import { branchIconFor } from '../shared/Icons.jsx'
import { useSelectionBranches } from './useSelectionBranches.js'
import { ensureCatalogue } from './canvas/ragoneCatalogue.js'

// Exported so other entry points (IonHome's "Find the right cell" branch
// picker) reuse the same curated branch list instead of duplicating it.
//
// THREE, not six (2026-08-06, BA). Automotive, BESS and Drone were seeded before
// MS's MATLAB reference scripts existed, so their weights are ours rather than
// his — a customer answering their questions gets a ranking nobody has checked
// against the physics. They are deactivated server-side by
// `20260806_three_active_flows`; the rows stay, so re-enabling one is a toggle in
// Admin → AI Flows.
//
// Removing a chip here is NOT enough on its own: useSelectionBranches appends any
// ACTIVE server branch missing from this list as a generic chip, so a branch
// dropped here but left enabled would come back under a default label. The
// server flag is what hides it; this list is what stops the fallback (fetch
// failure → the full curated list) from offering it anyway.
export const BRANCHES = [
  {
    slug: 'power_tool',
    label: 'Power Tool',
    prompt: "I'm choosing a cell for a power tool (cordless drill / saw / impact driver).",
  },
  {
    slug: 'ebike',
    label: 'eBike',
    prompt: "I'm choosing a cell for an eBike battery pack.",
  },
  {
    // Last on purpose: the named branches ask sharper questions, so a customer
    // whose application IS one of them should pick it. This is the door for
    // everyone else — who previously got a conversation with no fixed
    // arithmetic behind it.
    slug: 'general',
    label: 'Something else',
    prompt:
      "I'm choosing a cell but my application isn't one of the listed ones — " +
      'I can describe the pack I need.',
  },
]

/**
 * Seed a fresh guided-selection conversation: clears whatever chat was open,
 * resets the Sales-Selection context, records the branch (if given), fills
 * the draft and queues the autosend, and warm-loads the full cell catalogue.
 * Pure state — does NOT navigate. `gotoAssistant` below (the CellSelectionEntry
 * door) calls this and then navigates to /assistant; IonHome's "Find the
 * right cell" entry tile calls this directly since it's already rendered ON
 * /assistant (no navigation needed) — this is the shared seeding pattern
 * both reuse rather than duplicate.
 */
export function seedGuidedSelection(prompt, branchSlug = null, branchLabel = null) {
  // Every entry starts a NEW selection → begin a fresh conversation rather
  // than appending to whatever chat was open. clearConversation() also resets
  // draftInput, so set the prompt AFTER it.
  clearConversation()
  resetSelectionContext()
  if (branchSlug) {
    setSelectionContext({
      branch: branchSlug,
      branchLabel: branchLabel || null,
      stage: 'identify',
    })
  }
  draftInput.value = prompt
  // Auto-send on arrival at /assistant — AssistantPage consumes this once on
  // mount (guarded). Both the chip path and the free-text "Ask Ion" path
  // funnel through here, so both auto-start the flow.
  autoSendDraft.value = { text: prompt, contextHint: null }
  // Warm-load the FULL cell catalogue NOW, while we're still in the working
  // /cell-selection context, so the module-level `catalogueCells` signal is
  // populated (or in-flight) before SalesRagonePlot mounts on /assistant. The
  // guided plot reads that signal reactively and calls ensureCatalogue() on
  // mount too; without this pre-seed it mounted to an empty catalogue and
  // rendered only the ~22-cell ranking overlay instead of the full ~315.
  // ensureCatalogue is idempotent/deduped, so the plot's own call just joins
  // this load. Fire-and-forget — must NOT block navigation.
  ensureCatalogue()
}

export function CellSelectionEntry() {
  const [, navigate] = useLocation()
  const [text, setText] = useState('')
  const inputRef = useRef(null)
  const [cellCount, setCellCount] = useState(null)
  // Curated chips merged with the DB-driven active branch set (admin AI Flows).
  const branches = useSelectionBranches(BRANCHES)

  // Live catalogue size for the "ranks N+ cells" blurb — cached public endpoint,
  // rounded down to the nearest 10; falls back to "270+" until it resolves.
  useEffect(() => {
    let alive = true
    getCellInfo()
      .then((info) => {
        if (alive && info && Number.isFinite(info.count)) setCellCount(info.count)
      })
      .catch(() => {})
    return () => {
      alive = false
    }
  }, [])
  const cellCountLabel = cellCount != null ? `${Math.floor(cellCount / 10) * 10}+` : '270+'

  // Hide the Adaptive Sidebar on the entry landing — the two-door entry reads
  // cleaner without the filter rail. Mirrors how BenchmarkingWorkflow collapses
  // it; restored on unmount (e.g. when entering the Advanced wizard).
  useEffect(() => {
    const sidebar = document.querySelector('.adaptive-sidebar')
    const previousSidebarState = sidebarVisible.value
    if (sidebar) {
      sidebar.setAttribute('data-workflow-mode', 'active')
      sidebar.classList.add('workflow-hidden', 'adaptive-sidebar--collapsed')
      sidebar.classList.remove('workflow-visible')
    }
    return () => {
      const sb = document.querySelector('.adaptive-sidebar')
      if (sb) {
        sb.removeAttribute('data-workflow-mode')
        sb.classList.remove('workflow-hidden', 'workflow-visible', 'adaptive-sidebar--collapsed')
      }
      sidebarVisible.value = previousSidebarState
    }
  }, [])

  // Ion off (e.g. production, where the Guided·Ion door is gated away by
  // ionHidden): there is no Guided door left to show, so skip the now
  // single-door entry entirely and drop the user straight into the classic
  // Advanced filter+ranking wizard — the pre-Ion manual flow. This keeps
  // production from ever rendering a lopsided one-door landing. Reacts to
  // ionHidden so it fires once Ion availability resolves (it stays false while
  // loading, so no premature redirect on staging where Ion is on); `replace` so
  // the Back button doesn't bounce the user onto this redirect.
  // `!GUIDED_SELECTION_ENABLED` (EVERY build since 2026-08-27, dev included)
  // forces the same redirect as Ion-off, decoupled from Ion visibility: even
  // with Ion live for PRO/DEMO, no build shows the guided two-door landing —
  // it lands straight in the classic Advanced wizard.
  useEffect(() => {
    if (ionHidden.value || !GUIDED_SELECTION_ENABLED) {
      currentStep.value = 1
      navigate(appendDemoParam('/cell-selection?step=fundamentals'), { replace: true })
    }
  }, [ionHidden.value])

  function gotoAssistant(prompt, branchSlug = null, branchLabel = null) {
    seedGuidedSelection(prompt, branchSlug, branchLabel)
    // Preserve the ?ion_demo flag so the staging-demo walkthrough stays in
    // demo mode across the hop to /assistant.
    navigate(appendDemoParam('/assistant'))
  }

  function onSubmit(e) {
    e?.preventDefault()
    const trimmed = text.trim()
    if (!trimmed) {
      inputRef.current?.focus()
      return
    }
    gotoAssistant(trimmed)
  }

  function onKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      onSubmit()
    }
  }

  function openAdvancedWizard() {
    // Enter the classic filter + ranking wizard at step 1. MainAppPage swaps the
    // entry for BenchmarkingView once ?step is in the URL.
    currentStep.value = 1
    navigate(appendDemoParam('/cell-selection?step=fundamentals'))
  }

  // Ion off OR guided flow disabled (prod) → the useEffect above forwards to
  // the Advanced wizard. Render nothing in the meantime so the guided landing
  // never flashes.
  if (ionHidden.value || !GUIDED_SELECTION_ENABLED) return null

  return (
    <section class="cell-selection-entry" aria-labelledby="cell-selection-entry-title">
      <header class="cell-selection-entry__head">
        <span class="cell-selection-entry__crumb">Cell Selection</span>
        <h1 id="cell-selection-entry-title" class="cell-selection-entry__title">
          Find the right cell
        </h1>
      </header>

      <div class="cell-selection-entry__doors">
        {/* Guided · Ion (primary) — gated: hidden when Ion is off/unavailable
            for this user, per the Phase-3 gating rule. The Advanced door below
            stays visible regardless (it's the prod non-Ion flow). */}
        {!ionHidden.value && (
          <div class="cell-selection-entry__door cell-selection-entry__door--guided">
            <div class="cell-selection-entry__label">Guided · Ion</div>
            <h2 class="cell-selection-entry__heading">Describe your application</h2>
            <p class="cell-selection-entry__sub">
              Batemo Ion ranks {cellCountLabel} Li-ion cells on measured performance, then hands
              off to compare and benchmarking.
            </p>

            <form class="cell-selection-entry__form" onSubmit={onSubmit}>
              <input
                ref={inputRef}
                type="text"
                class="cell-selection-entry__input"
                placeholder="e.g. cordless drill, 100 A pulse, lightweight pack…"
                value={text}
                onInput={(e) => setText(e.currentTarget.value)}
                onKeyDown={onKeyDown}
                aria-label="Describe your application"
              />
              <button
                type="submit"
                class="cell-selection-entry__primary"
                disabled={!text.trim()}
                aria-label="Ask Ion"
              >
                Ask Ion →
              </button>
            </form>

            <div
              class="cell-selection-entry__chips"
              role="group"
              aria-label="Start from an application"
            >
              <span class="cell-selection-entry__chips-label">or start from an application:</span>
              {branches.map((b) => {
                const Icon = branchIconFor(b.slug)
                return (
                  <button
                    key={b.slug}
                    type="button"
                    class="cell-selection-entry__chip"
                    data-branch={b.slug}
                    onClick={() => gotoAssistant(b.prompt, b.slug, b.label)}
                  >
                    <span class="cell-selection-entry__chip-icon" aria-hidden="true">
                      <Icon size={18} />
                    </span>
                    <span class="cell-selection-entry__chip-label">{b.label}</span>
                  </button>
                )
              })}
            </div>
          </div>
        )}

        {/* Advanced filter + ranking (equal weight) */}
        <div class="cell-selection-entry__door cell-selection-entry__door--advanced">
          <div class="cell-selection-entry__label cell-selection-entry__label--muted">Advanced</div>
          <h2 class="cell-selection-entry__heading">Know your specs?</h2>
          <p class="cell-selection-entry__sub">
            Filter by format, chemistry and operating point, then rank the catalogue yourself.
          </p>
          <button
            type="button"
            class="cell-selection-entry__ghost"
            onClick={openAdvancedWizard}
          >
            Open filter &amp; ranking →
          </button>
        </div>
      </div>
    </section>
  )
}

export default CellSelectionEntry
